import React, { useEffect } from "react";
import Button from "../../../../components/staff/Button";
import InputText from "../../../../components/staff/InputText";
import DropDownMultiple from "../../../../components/staff/DropDownMultiple";
import DatePicker from "../../../../components/staff/DatePicker";
import ToogleSwitch from "../../../../components/staff/ToogleSwitch";
import SaveChanges from "../../../../components/Atomos/Icons/EditMode/SaveChanges";
import { PaymentMethod } from "../../../../Enums/RegistrationEnum";
import { useEditParticipantForm } from "../hooks/useEditParticipantForm";
import { useEditParticipantSave } from "../hooks/useEditParticipantSave";
import { formatToCurrency } from "../formatters";
import InputTextArea from "../../../../components/staff/InputTextArea";

export default function NewPaymentSection (): React.ReactElement {
  const {
    paymentFieldsDisabled,
    newPaymentDate,
    newPaymentAmount,
    newPaymentMethod,
    newPaymentNote,
    applyFirmCredit,
    creditPayment,
    newPaymentMethodOptions,
    prepaidPaymentAmount,
    hasPrepaidPayment,
    payableBalance,
    setNewPaymentDate,
    setNewPaymentAmount,
    setNewPaymentMethod,
    setNewPaymentNote,
    setApplyFirmCredit,
    setCreditPayment,
    additionalFeeInput,
    setAdditionalFeeInput,
    amountRefunded,
    setAmountRefunded,
    patchRegistration,
  } = useEditParticipantForm();
  const { isSaving, handleSaveRegistration } = useEditParticipantSave();

  const selectedNewPaymentMethod = newPaymentMethod || null;
  const isPrepaidSelected = selectedNewPaymentMethod === "prepaid";
  const maxPaymentAmount = Math.max(0, payableBalance);
  const firmCreditBalance = 0;
  const firmCreditUnavailable = firmCreditBalance <= 0;

  useEffect(() => {
    if (firmCreditUnavailable && applyFirmCredit) {
      setApplyFirmCredit(false);
    }
  }, [applyFirmCredit, firmCreditUnavailable, setApplyFirmCredit]);

  const handlePaymentAmountChange = (value: string) => {
    const trimmed = value.trim();
    if (
      trimmed === "" ||
      trimmed === "-" ||
      trimmed === "." ||
      trimmed === "-."
    ) {
      setNewPaymentAmount(value);
      return;
    }
    const parsed = parseFloat(trimmed);
    if (Number.isNaN(parsed)) {
      setNewPaymentAmount(value);
      return;
    }
    if (parsed > maxPaymentAmount) {
      setNewPaymentAmount(String(maxPaymentAmount));
      return;
    }
    if (parsed < 0) {
      return;
    }
    setNewPaymentAmount(value);
  };

  return (
    <div className="flex flex-col gap-4">
      <h3 className="w-full text-secondary">
        New Payment
      </h3>
      {hasPrepaidPayment && (
        <p className="text-sm text-gray-600">
          Prepaid already covers the event fee ({formatToCurrency(prepaidPaymentAmount)}).
          Additional amounts must use Credit Card, Wire Transfer, or Check.
        </p>
      )}
      {isPrepaidSelected && !hasPrepaidPayment && (
        <p className="text-sm text-gray-600">
          Prepaid defaults to the regular event member fee ({formatToCurrency(prepaidPaymentAmount)}),
          never a late fee. You can type a different amount if needed.
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <DatePicker
          title="Payment Date"
          value={newPaymentDate}
          onChange={setNewPaymentDate}
          disabled={paymentFieldsDisabled}
          showAsLabel={paymentFieldsDisabled}
        />
        <InputText
          title="Payment Amount"
          type="number"
          value={newPaymentAmount}
          onChange={handlePaymentAmountChange}
          prefix="$"
          max={maxPaymentAmount}
          disabled={paymentFieldsDisabled}
          showAsLabel={paymentFieldsDisabled}
        />
        <DropDownMultiple
          title="Payment Method"
          options={newPaymentMethodOptions}
          value={selectedNewPaymentMethod}
          onChange={(option) => {
            const next = (option?.id as PaymentMethod | undefined) ?? "";
            setNewPaymentMethod(next);
            if (next) patchRegistration({ paymentMethod: next });
          }}
          required
          disabled={paymentFieldsDisabled}
          showAsLabel={paymentFieldsDisabled}
        />
        <InputText
          title="Additional Fees"
          type="number"
          value={additionalFeeInput.trim() ? additionalFeeInput : "0"}
          onChange={setAdditionalFeeInput}
          prefix="$"
          min={0}
          disabled
          showAsLabel
        />
        <InputText
          title="Amount Refunded"
          type="number"
          value={amountRefunded}
          onChange={setAmountRefunded}
          prefix="$"
          disabled
          showAsLabel
        />
        {/* <div className="col-span-5 grid grid-cols-5 gap-6"> */}
        <InputText
          title="Firm Credit Balance"
          type="number"
          value={String(firmCreditBalance)}
          onChange={() => { }}
          prefix="$"
          disabled
          showAsLabel
        />
        <ToogleSwitch
          label="Apply Firm Credit"
          labelTop
          checked={applyFirmCredit && !firmCreditUnavailable}
          onChange={(checked) => {
            if (firmCreditUnavailable) {
              return;
            }
            setApplyFirmCredit(checked);
          }}
          disabled={paymentFieldsDisabled || firmCreditUnavailable}
          className="!mb-2 "
        />
        <InputText
          title="Credit Payment"
          type="number"
          value={creditPayment.trim() ? creditPayment : "0"}
          onChange={setCreditPayment}
          prefix="$"
          disabled={paymentFieldsDisabled || !applyFirmCredit}
          showAsLabel={paymentFieldsDisabled || !applyFirmCredit}
        />
        <InputTextArea
          title="Notes Payment"
          value={newPaymentNote}
          onChange={setNewPaymentNote}
          className="flex-1"
          disabled={paymentFieldsDisabled}
        />
        <Button
          text="Register Payment"
          onClick={() => {
            void handleSaveRegistration(false);
          }}
          color="secondary"
          variant="filled"
          size="fixed"
          className="mt-auto"
          icon={<SaveChanges />}
          disabled={paymentFieldsDisabled || isSaving}
        />
      </div>
    </div>
  );
}
